import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { FloatingPanel } from './floating-panel';
import { Button } from './button';

export interface ConfirmOptions {
  title: string;
  /** Qué se va a tocar y qué pasa después, en una línea. Nada de «¿Seguro?». */
  description?: string;
  /** Texto del botón que ejecuta: «Eliminar definitivamente», no «Aceptar». */
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface Pendiente {
  id: number;
  options: ConfirmOptions;
  resolve: (confirmado: boolean) => void;
}

/**
 * Confirmación de una acción de listado en ventana flotante movible, sin fondo bloqueante.
 *
 * Se usa como `window.confirm` (`if (!(await confirm({...}))) return;`), pero el usuario puede mover
 * la ventana y seguir viendo la fila que está mirando. El `dialog` hay que pintarlo en el componente.
 *
 * La pregunta abierta vive en un ref y no en el estado: así una segunda pregunta resuelve la
 * primera como «no» —nadie se queda esperando una promesa que ya no va a llegar— y el desmontaje
 * las cancela todas.
 */
export function useConfirm(): {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  dialog: ReactNode;
} {
  const [pendiente, setPendiente] = useState<Pendiente | null>(null);
  const pendienteRef = useRef<Pendiente | null>(null);
  const contador = useRef(0);

  const confirm = useCallback((options: ConfirmOptions) => new Promise<boolean>((resolve) => {
    // Solo puede haber una ventana de confirmación: la anterior se cancela.
    pendienteRef.current?.resolve(false);
    contador.current += 1;
    const nuevo = { id: contador.current, options, resolve };
    pendienteRef.current = nuevo;
    setPendiente(nuevo);
  }), []);

  const cerrar = useCallback((confirmado: boolean) => {
    const actual = pendienteRef.current;
    pendienteRef.current = null;
    setPendiente(null);
    actual?.resolve(confirmado);
  }, []);

  // Si el componente desaparece con una pregunta abierta, la promesa se resuelve en «no».
  useEffect(() => () => { pendienteRef.current?.resolve(false); pendienteRef.current = null; }, []);

  const dialog = pendiente ? (
    <FloatingPanel
      // Cada pregunta es un panel nuevo: así el foco previo es el botón de **esta** fila y no el
      // de la pregunta anterior.
      key={pendiente.id}
      open
      onClose={() => cerrar(false)}
      title={pendiente.options.title}
      description={pendiente.options.description}
      width={400}
      footer={
        <>
          {/* En una acción destructiva el foco arranca en la salida segura, no en el botón rojo:
              Escape y Enter por descuido no deberían borrar nada. */}
          <Button variant="outline" data-autofocus onClick={() => cerrar(false)}>
            {pendiente.options.cancelLabel ?? 'Cancelar'}
          </Button>
          <Button
            variant={pendiente.options.destructive ? 'destructive' : 'default'}
            onClick={() => cerrar(true)}
          >
            {pendiente.options.confirmLabel ?? 'Continuar'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted-foreground">
        {pendiente.options.description ?? 'Se aplicará el cambio ahora mismo.'}
      </p>
    </FloatingPanel>
  ) : null;

  return { confirm, dialog };
}
